<template>
  <div class="checkout-items">
    <!-- loaded -->
    <template v-if="isLoaded">
      <!-- KP-4363: search the products of this order -->
      <div
        v-if="isProductSearchEnabled"
        class="mb-2 order-products-search"
      >
        <b-form-input
          v-model="productSearch"
          type="search"
          placeholder="Search products in this order"
          aria-label="Search products in this order"
        />
      </div>
      <!-- has product -->
      <template v-if="productsSlice.length">
        <div
          v-if="order && order.isEnableShipmentFeature && order.readyToShipProductsCount && !isShipment"
          class="w-100 d-flex justify-content-end mb-2"
        >
          <div
            v-if="order.readyToShipProductsCount > 1 || isPartiallyConfirmedOrder(order)"
            class="mr-1"
          >
            <b-form-checkbox
              class="mt-1"
              :checked-value="true"
              :unchecked-value="false"
              :checked="isSelectedAll()"
              @change="onSelectReadyToShipProducts($event)"
            >
              {{ getShipSelectAllLabel(order) }}
            </b-form-checkbox>
          </div>
          <div>
            <b-button
              class="border-rounded"
              variant="info"
              :disabled="!isSelectedProduct"
              @click="shipProducts"
            >
              Ship Now
            </b-button>
          </div>
        </div>
        <!-- KP-4363: select pending products to confirm -->
        <div
          v-if="hasPendingProductsToConfirm"
          class="w-100 d-flex justify-content-end mb-2"
        >
          <b-form-checkbox
            class="mt-1"
            :checked="isAllPendingSelected(visibleOrderProducts)"
            :checked-value="true"
            :unchecked-value="false"
            @change="toggleAllPendingForConfirm(visibleOrderProducts, $event)"
          >
            Select all pending products
          </b-form-checkbox>
        </div>
        <div
          v-for="(product, index) in productsSlice"
          :key="product.productId"
          class="product-card-wrapper"
          :class="{ 'expandable-product': isPartiallyShipped(product) && hasShippedItems(product) }"
        >
          <b-row
            class="mx-0"
            :class="{
              'cart-product mb-2 border-info-thin':
                product.status ===
                ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
              'cart-product mb-2':
                product.status !==
                ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
              'has-error' : isRemoveEnabled(product) && product.hasError,
              'has-discount': hasProductDiscount(product),
            }"
            no-body
          >
            <!-- Product Image -->
            <b-col class="item-img col-md-3 col-xl-2 pl-0 col-12">
              <b-link
                :to="{name: 'product-detail', params: {id: product.productId, collectionId:order.collectionId}}"
              >
                <b-img-lazy
                  class="imgcss"
                  :src="parseProductImage(product)"
                  :alt="`${product.name}-${product.productId}`"
                  @error.native="imageLoadError"
                />
              </b-link>
              <span
                v-if="product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL && (product.isDeletedByRetailer || product.isDeletedByBrand)"
                class="d-block text-center msgposition remove-span"
              >
                This product is removed by {{ product.isDeletedByRetailer ? 'the retailer' : 'the brand' }}.
              </span>
              <span
                v-if="!(product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL) && product.isEdited"
                class="d-block text-center msgposition modify-span"
              >
                This product is modified by the brand.
              </span>
            </b-col>

            <!-- Product Details: Card Body -->
            <b-col class="col-md-7 col-xl-8 border-right pt-2 d-flex flex-column col-12">
              <div class="item-name d-flex justify-content-between">
                <div>
                  <b-link
                    class="product-link"
                    :to="{name: 'product-detail', params: {id: product.productId, collectionId:order.collectionId}}"
                  >
                    {{ product.name }}
                  </b-link>
                  <b-link class="company-name">
                    <b-breadcrumb
                      class="breadcrumb-products mb-0 pl-0"
                      :items="[product.department, product.category, product.subCategory]"
                    />
                  </b-link>
                  <div class="cart-price-title">
                    <div class="mt-75">
                      Style Code: <span class="cart-price-value">{{ product.styleCode }}</span>
                    </div>
                    <div>
                      Retail Price: <span class="cart-price-value">{{ formatCurrency(product.retailPrice, currency) }}</span>
                    </div>
                    <div>
                      Wholesale Selling Price:
                      <span
                        class="cart-price-value"
                        :class="{ 'text-success': hasProductDiscount(product) }"
                      >
                        {{ formatCurrency(product.price, currency) }}
                      </span>
                    </div>
                    <div v-if="order && !isRetailer && product.purchasePrice">
                      Kingpin Purchase Price:
                      <span class="cart-price-value">{{ formatCurrency(product.purchasePrice, currency) }}</span>
                    </div>
                    <!-- Product Discount - Brand Only -->
                    <div
                      v-if="isDiscountEnabled && !isProductDiscountDisabled(product)"
                      class="mt-1"
                    >
                      <!-- View Mode: Has discount and not editing -->
                      <div
                        v-if="hasProductDiscount(product) && !isEditingDiscount(product)"
                        class="d-flex align-items-center"
                      >
                        <span class="cart-price-title">Discount:</span>
                        <b-badge
                          variant="success"
                          class="ml-50 mr-50"
                        >
                          -{{ Math.round((1 - product.discount) * 100) }}%
                        </b-badge>
                        <b-button
                          size="sm"
                          variant="flat-secondary"
                          class="btn-icon p-25"
                          :disabled="isApplyingDiscount"
                          @click="startEditDiscount(product)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="14"
                          />
                        </b-button>
                      </div>

                      <!-- Edit Mode -->
                      <div v-else-if="isEditingDiscount(product)">
                        <span class="cart-price-title">Discount %:</span>
                        <div class="d-flex align-items-center mt-50">
                          <b-form-input
                            v-model="productDiscounts[product.productId]"
                            type="number"
                            size="sm"
                            step="1"
                            min="0"
                            max="100"
                            placeholder="e.g., 15"
                            class="discount-input"
                            :disabled="isApplyingDiscount"
                          />
                          <span class="ml-50">%</span>
                          <b-button
                            v-if="productDiscounts[product.productId] !== '' && productDiscounts[product.productId] !== undefined"
                            size="sm"
                            variant="primary"
                            class="ml-1"
                            :disabled="isApplyingDiscount"
                            @click="applyProductDiscount(product)"
                          >
                            Apply
                          </b-button>
                          <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="ml-1"
                            :disabled="isApplyingDiscount"
                            @click="cancelEditDiscount(product)"
                          >
                            Cancel
                          </b-button>
                        </div>
                        <small class="text-muted">Enter discount percentage (0-100)</small>
                      </div>

                      <!-- No discount yet: Show Edit button to add -->
                      <div
                        v-else
                        class="d-flex align-items-center"
                      >
                        <span class="cart-price-title">Discount:</span>
                        <b-button
                          size="sm"
                          variant="flat-secondary"
                          class="btn-icon p-25 ml-50"
                          :disabled="isApplyingDiscount"
                          @click="startEditDiscount(product)"
                        >
                          <feather-icon
                            icon="EditIcon"
                            size="14"
                          />
                        </b-button>
                      </div>
                    </div>
                    <!-- Product Discount - Retailer View (Read-Only) -->
                    <div
                      v-if="isRetailer && hasProductDiscount(product) && !isShipment"
                      class="mt-1"
                    >
                      <div class="d-flex align-items-center">
                        <span class="cart-price-title">Discount:</span>
                        <b-badge
                          variant="success"
                          class="ml-50"
                        >
                          -{{ Math.round((1 - product.discount) * 100) }}%
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-right">
                  <div
                    v-if="order && isShowStatusTag(product.status)"
                    class="text-light font-weight-bold d-inline-flex align-items-center"
                  >
                    <b-card-text
                      class="product-status-tag"
                      :style="{ background: getStatusTextAndColorCode(product.status, order).colorCode }"
                    >
                      {{ getLineStatusText(product, order) }}
                      <feather-icon
                        icon="AlertCircleIcon"
                        size="18"
                      />
                    </b-card-text>
                  </div>
                  <div class="font-weight-normal mt-50">
                    <span
                      v-if="product.shippingStartDate && product.shippingEndDate"
                      class="mb-2 cart-price-title"
                    >
                      Availability Date:
                      <span class="cart-price-value">{{ formattedDate(product.shippingStartDate) }} to {{ formattedDate(product.shippingEndDate) }}</span>
                    </span>
                  </div>
                  <div
                    v-if="product.isCarton"
                    class="item-quantity mt-1 mr-1"
                  >
                    <span class="cart-price-title">Qty:</span>
                    <b-form-spinbutton
                      v-model="product.quantity"
                      size="sm"
                      class="ml-75"
                      :class="{ 'carton-qty-invalid': isCartonExceedingShipmentQty(product) }"
                      inline
                      :max="getCartonMaxQty(product)"
                      :disabled="isCartonInputDisabled(product)"
                      @input="onCartonQuantityChange(product)"
                    />
                    <div
                      v-if="shouldShowCartonMaxQty(product)"
                      class="text-nowrap text-right color-actions-text-grey mt-2-px mr-1"
                    > Pending to Ship: {{ getCartonDisplayMaxQty(product) }} </div>
                  </div>
                </div>
              </div>

              <!-- variants -->
              <product-variants
                :disabled-product="isProductDisabled(product)"
                :is-carton="product.isCarton"
                :items="getProductVariantItems(product)"
                :is-modify-enabled-brand="isModifyEnabledForBrand()"
                :is-shipment-settings-modal="isShipmentSettingsModal"
                :show-quantity-exceed-validation="shouldValidateShipmentQty()"
                @update-variants="
                  (variants) => updateProductVariants(variants, product)
                "
              />

            </b-col>

            <!-- Product Options/Actions -->
            <b-col class="item-options text-center d-flex flex-column flex-md-column md-0 ml-md-auto col-md-2 col-xl-2 align-items-center mt-2 col-12">
              <div
                v-if="order && order.isEnableShipmentFeature"
                class="ml-auto mr-1"
              >
                <b-card-text v-if="isLineShipSelectable(product, order)">
                  <b-form-checkbox
                    :id="isShipment ? product.productId + 's' : product.productId"
                    class="shipment-checkbox"
                    :checked="
                      product.status ===
                        ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
                    "
                    :checked-value="true"
                    :unchecked-value="false"
                    @change="onSelect($event, product, index)"
                  />
                </b-card-text>
              </div>
              <div
                v-if="isLineSelectableForConfirm(product, order, isShipment)"
                class="ml-auto mr-1"
              >
                <b-form-checkbox
                  :id="`partial-confirm-${product.productId}`"
                  class="shipment-checkbox"
                  :checked="isLineSelectedForConfirm(product)"
                  :checked-value="true"
                  :unchecked-value="false"
                  @change="toggleLineForConfirm(product, $event)"
                />
              </div>
              <template v-if="order">
                <b-button
                  v-if="isRemoveEnabled(product)"
                  v-b-modal="`modal-remove-product-${product.productId}`"
                  variant="danger"
                  class="p-0 mt-1 remove-wishlist remove-button-height w-75"
                >
                  <feather-icon
                    icon="XIcon"
                    class="mr-1"
                  />
                  <span>Remove</span>
                </b-button>
              </template>
              <b-modal
                :id="`modal-remove-product-${product.productId}`"
                ok-variant="danger"
                ok-title="Remove product"
                cancel-title="Dismiss"
                modal-class="modal-danger"
                centered
                title="Remove this product?"
                @ok="$emit('remove-product', product, index)"
              >
                <b-card-text>
                  Are you sure you want to delete this product?
                </b-card-text>
              </b-modal>

              <div class="item-wrapper my-2 my-md-auto ml-auto ml-md-0">
                <div class="item-cost text-center d-flex d-md-block">
                  <h4
                    v-if="variantLoaded"
                    class="item-price mr-50 mr-md-0  font-weight-bolder color-neutral-black"
                  >
                    Total Value:
                    {{ formatCurrency(product.lineTotalPrice, currency) }}
                  </h4>
                  <b-card-text
                    v-if="variantLoaded"
                    class="mt-2"
                  >
                    Total Units:
                    {{ formatNumber(product.lineTotalQuantity || 0) }}
                  </b-card-text>
                </div>
              </div>
            </b-col>
            <!-- Expand button at bottom for partially shipped products -->
            <b-col
              v-if="isPartiallyShipped(product) && hasShippedItems(product)"
              cols="12"
              class="expand-row py-2"
              @click="togglePartialBreakdown(product.productId)"
            >
              <div class="d-flex justify-content-center align-items-center">
                <b-badge
                  variant="success"
                  class="mr-2"
                >
                  Shipped: {{ product.shippedQuantity || 0 }}
                </b-badge>
                <b-badge
                  variant="warning"
                  class="mr-3"
                >
                  Remaining: {{ getRemainingUnits(product) }}
                </b-badge>
                <span class="text-info expand-text">
                  {{ expandedPartialProducts[product.productId] ? 'Hide Details' : 'View Details' }}
                  <feather-icon
                    :icon="expandedPartialProducts[product.productId] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="16"
                    class="ml-50"
                  />
                </span>
              </div>
            </b-col>
          </b-row>

          <!-- Child Shipment Details Card -->
          <b-collapse
            v-if="isPartiallyShipped(product) && hasShippedItems(product)"
            :id="`shipment-card-${product.productId}`"
            v-model="expandedPartialProducts[product.productId]"
          >
            <div class="shipment-child-card ml-3 ml-md-4 mb-2 p-1">
              <!-- Carton Products: Simple quantity display -->
              <template v-if="product.isCarton">
                <div class="d-flex justify-content-around py-2">
                  <div class="text-center">
                    <div class="section-header text-success mb-50">
                      Shipped Cartons
                    </div>
                    <h4 class="text-success font-weight-bold mb-0">
                      {{ product.shippedQuantity || 0 }}
                    </h4>
                  </div>
                  <div class="text-center">
                    <div class="section-header text-warning mb-50">
                      Remaining Cartons
                    </div>
                    <h4 class="text-warning font-weight-bold mb-0">
                      {{ getRemainingUnits(product) }}
                    </h4>
                  </div>
                </div>
              </template>
              <!-- Non-Carton Products: Variants table display -->
              <template v-else>
                <!-- Shipped Section -->
                <div class="shipped-section">
                  <div class="section-header text-success">
                    Shipped
                  </div>
                  <product-variants
                    :disabled-product="true"
                    :is-carton="product.isCarton"
                    :items="getShippedItemsForDisplay(product)"
                    :is-modify-enabled-brand="false"
                    class="shipped-variants-table compact-variants"
                  />
                </div>
                <!-- Remaining Section -->
                <div class="remaining-section">
                  <div class="section-header text-warning">
                    Remaining
                  </div>
                  <product-variants
                    :disabled-product="true"
                    :is-carton="product.isCarton"
                    :items="getRemainingItemsForDisplay(product)"
                    :is-modify-enabled-brand="false"
                    class="remaining-variants-table compact-variants"
                  />
                </div>
              </template>
            </div>
          </b-collapse>
        </div>
      </template>
      <!-- empty order products -->
      <template v-else>
        <b-card
          :title="productSearchQuery || isFilterApplied ? 'No products found' : 'No Products Added'"
        >
          {{ emptyProductsText }}
        </b-card>
      </template>
    </template>
    <!-- loading -->
    <template v-else>
      <b-card class="text-center p-4">
        <b-spinner />
      </b-card>
    </template>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  BBadge,
  BBreadcrumb,
  BButton,
  BCard,
  BCardText,
  BCollapse,
  BFormCheckbox,
  BFormSpinbutton,
  BFormInput,
  BImgLazy,
  BLink,
  BSpinner,
  BRow,
  BCol,
  BModal,
  VBModal,
  VBTooltip,
} from 'bootstrap-vue'
import { utils, constants as c, productUtil } from '@kingpin-global/kingpin-utils-frontend'
import {
  ref, onMounted, onUnmounted,
} from '@vue/composition-api'
import ProductVariants from '@/views/apps/product/detail/ProductVariants.vue'
import {
 formatNumber, hasProductMaxQuantity, isProductValueDisabled, getMaxQuantity, imageLoadError,
} from '@/@core/utils/utils'
import { getStatusTextAndColorCode } from '@/common-utils'
import { getLineStatusText, isLineShipSelectable } from '@/composables/orderLineStatusText'
import constants, { PRODUCTS_LIMIT } from '@/constants'
import { useOrderPartialConfirm, isOrderLineRemovable } from '@/composables/useOrderPartialConfirm'
import { debounce } from 'lodash'

import { formatDate } from '@/@core/utils/filter'

import store from '@/store'
import { UPDATE_ORDER_PRODUCTS_DISCOUNT } from '@/store/modules/order.module'
import { apiToastSuccess, apiToastWarning, apiToastErrorV2 } from '@/@core/utils/toast'
import { filterOrderProductsBySearch } from './order-product-search'
import { getShipSelectAllLabel } from './order-utils'
import { useEcommerceUi } from '../useEcommerce'
import UserRoleMixinVue from '../UserRoleMixin.vue'
import { parseProductItems } from '../checkout/cart-products-utils'

const { getProductAndOrderTotals } = productUtil
const { formatCurrency, formattedDate } = utils
const { ORDER_STATUS, SHIPMENT_STATUS } = c
const { ORDER_PRODUCT_SHIPMENT_STATUS } = constants

export default {
  name: 'OrderProducts',
  components: {
    BBadge,
    BCard,
    BCardText,
    BCollapse,
    BFormCheckbox,
    BLink,
    BImgLazy,
    BButton,
    BFormSpinbutton,
    BFormInput,
    BBreadcrumb,
    BSpinner,
    BRow,
    BCol,
    ProductVariants,
    BModal,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  mixins: [UserRoleMixinVue],
  props: {
    order: {
      type: Object,
      default() {
        return null
      },
    },
    orderProducts: {
      type: Array,
      default() {
        return null
      },
    },
    isShipment: {
      type: Boolean,
      default() {
        return false
      },
    },
    isShipmentSettingsModal: {
      type: Boolean,
      default() {
        return false
      },
    },
    isModifyEnabled: {
      type: Boolean,
      default() {
        return false
      },
    },
    isFilterApplied: {
      type: Boolean,
      default() {
        return false
      },
    },
  },
  data() {
    return {
      getStatusTextAndColorCode,
      getLineStatusText,
      isLineShipSelectable,
      ORDER_PRODUCT_SHIPMENT_STATUS,
      ORDER_STATUS,
      formatDate,
      isProductValueDisabled,
      hasProductMaxQuantity,
      getMaxQuantity,
      imageLoadError,
      productDiscounts: {},
      isApplyingDiscount: false,
      editingDiscountProductIds: {},
      originalDiscounts: {},
      expandedPartialProducts: {},
      productSearch: '',
    }
  },
  computed: {
    isProductSearchEnabled() {
      return !this.isShipment && !!(this.orderProducts || []).length
    },
    // KP-4363: the products the search leaves visible ("select all" acts on these).
    visibleOrderProducts() {
      return filterOrderProductsBySearch(this.orderProducts || [], this.productSearchQuery)
    },
    emptyProductsText() {
      if (this.productSearchQuery) return 'No products match your search'
      return this.isFilterApplied ? 'No matches were found. Try a new filter.' : 'No products in your order'
    },
    currency() {
      if (this.isBrand) {
        return this.order.currencyForBrand
      }
      return this.order.currency
    },
    isEditEnabled() {
      if (this.order) {
        return !this.isModifyEnabled
      }
      return this.isShipment
    },
    isDiscountEnabled() {
      if (!this.order) return false
      return this.isBrand
        && this.order.status !== ORDER_STATUS.CANCELLED
        && this.order.status !== ORDER_STATUS.REJECTED
        && !this.isShipment
    },
    hasPendingProductsToConfirm() {
      return this.isPartialConfirmEnabled(this.order, this.isShipment)
        && (this.orderProducts || []).some(this.isPendingConfirmLine)
    },
  },
  watch: {
    // Watching the variable when filter applied
    orderProducts: {
      handler(newVal, oldVal) {
        this.setOrderProducts()
        this.initProductDiscounts()
        // A filter change must not leave hidden products selected for confirm.
        // A ship tick changes the same list in place and must not drop it.
        if (newVal !== oldVal) this.clearConfirmSelection()
      },
      immediate: true,
    },
    productSearch(query) {
      this.debouncedSearchOrderProducts(query)
    },
    selectedProductIds: {
      handler() {
        const confirmIds = this.getSelectedForConfirmIds(this.orderProducts)
        // KP-4363: confirming and shipping are separate selections; picking
        // products to confirm drops the products picked for shipping.
        if (confirmIds.length && this.isSelectedProduct) {
          this.onSelectReadyToShipProducts(false)
        }
        this.$emit('partial-confirm-selection', confirmIds)
      },
      deep: true,
      immediate: true,
    },
    isSelectedProduct(isSelected) {
      // KP-4363: picking products to ship drops the products picked to confirm.
      if (isSelected) this.clearConfirmSelection()
    },
  },
  created() {
    this.debouncedSearchOrderProducts = debounce(query => this.searchOrderProducts(query), 200)
  },
  beforeDestroy() {
    this.debouncedSearchOrderProducts.cancel()
  },
  methods: {
    isProductDeletedOrCancelled(product) {
      const isCancelled = product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL
      return product.isDeleted || product.isDeletedByBrand || product.isDeletedByRetailer || isCancelled
    },
    isProductDisabled(product) {
      const isDeletedOrCancelled = this.isProductDeletedOrCancelled(product)
      if (this.order?.isDataForCreateShipment) {
        return isDeletedOrCancelled
      }
      return this.isShipment || isDeletedOrCancelled || this.isLockedConfirmedLine(product, this.order)
    },
    isShipmentCreationMode() {
      return !!(this.order && this.order.isDataForCreateShipment)
    },
    isCartonShipmentItem(product) {
      return this.isShipmentCreationMode() && product.maxShipmentQty !== undefined
    },
    // Whether quantity exceed validation should be shown
    // Only during shipment creation or for editable orders (not when viewing existing shipments)
    shouldValidateShipmentQty() {
      if (!this.order) return false
      // When viewing an existing shipment (not creating one) and not in shipment settings modal, skip validation
      if (this.isShipment && !this.order.isDataForCreateShipment && !this.isShipmentSettingsModal) return false
      return this.isShipmentSettingsModal
        || (!this.isShipment && this.order.status !== ORDER_STATUS.CONFIRMED && this.order.status !== ORDER_STATUS.REJECTED)
    },
    // Whether the carton quantity exceeds the max allowed for shipment
    isCartonExceedingShipmentQty(product) {
      return this.shouldValidateShipmentQty()
        && this.isCartonShipmentItem(product)
        && product.quantity > product.maxShipmentQty
    },
    getCartonMaxQty(product) {
      return this.isCartonShipmentItem(product) ? product.maxShipmentQty : this.getMaxQuantity(product.quantityAvailable)
    },
    // Get the display value for "Pending to Ship"
    // In shipment settings modal, prefer maxShipmentQty; otherwise fall back to quantityAvailable
    getCartonDisplayMaxQty(product) {
      if (this.isShipmentSettingsModal && product.maxShipmentQty != null) {
        return product.maxShipmentQty
      }
      return product.quantityAvailable != null ? product.quantityAvailable : ''
    },
    // Whether to show the "Pending to Ship" label for carton products
    // In shipment settings modal: show for cartons with maxShipmentQty or quantityAvailable
    // Otherwise: show when editing is enabled and product has max quantity
    shouldShowCartonMaxQty(product) {
      if (this.isShipmentSettingsModal) {
        return product.isCarton && (product.maxShipmentQty !== undefined || this.hasProductMaxQuantity(product.quantityAvailable))
      }
      return false
    },
    isCartonInputDisabled(product) {
      return product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL
        || this.isLockedConfirmedLine(product, this.order)
        || product.isEdited
        || (this.isEditEnabled && !this.isShipmentCreationMode())
        || this.isProductValueDisabled(product.quantityAvailable)
    },
    getProductVariantItems(product) {
      return !this.isShipment ? product.items : product.itemsForShipment || product.items
    },
    isModifyEnabledForBrand() {
      return !this.isEditEnabled || this.isShipmentCreationMode()
    },
    isRemoveEnabled(product) {
      return isOrderLineRemovable(product, this.order, { isBrand: this.isBrand, isRetailer: this.isRetailer })
    },
    isShowStatusTag(productStatus) {
      const orderProductStatuses = Object.values(ORDER_PRODUCT_SHIPMENT_STATUS).map(
        _ => _.LABEL,
      )
      const shipmentStatuses = Object.values(SHIPMENT_STATUS)
      return orderProductStatuses.includes(productStatus) || shipmentStatuses.includes(productStatus)
    },
    isProductDiscountDisabled(product) {
      // Disable discount for cancelled products
      if (product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL) {
        return true
      }
      // Disable discount for products in shipment flow
      const shipmentStatuses = Object.values(SHIPMENT_STATUS)
      if (shipmentStatuses.includes(product.status)) {
        return true
      }
      return false
    },
    hasProductDiscount(product) {
      // Check if discount exists and is less than 1 (meaning there's a discount)
      // discount = 1 means no discount, discount < 1 means discount applied
      // discount = 0 means 100% discount, discount = 0.85 means 15% discount
      return product.discount !== undefined && product.discount !== null && product.discount < 1
    },
    isEditingDiscount(product) {
      return !!this.editingDiscountProductIds[product.productId]
    },
    startEditDiscount(product) {
      // Store original value for cancel functionality
      this.$set(this.originalDiscounts, product.productId, this.productDiscounts[product.productId] || '')
      this.$set(this.editingDiscountProductIds, product.productId, true)
    },
    cancelEditDiscount(product) {
      // Revert to original value or clear if no original value existed
      const originalValue = this.originalDiscounts[product.productId]
      this.$set(this.productDiscounts, product.productId, originalValue !== undefined ? originalValue : '')
      this.$set(this.editingDiscountProductIds, product.productId, false)
    },
    async applyProductDiscount(product) {
      const discountPercent = parseFloat(this.productDiscounts[product.productId])
      const isValidDiscount = !Number.isNaN(discountPercent) && discountPercent >= 0 && discountPercent <= 100
      if (!isValidDiscount) {
        apiToastWarning('Please enter a valid discount percentage (0-100)')
        return
      }
      // Convert percentage to multiplier (e.g., 15% -> 0.85, 0% -> 1, 100% -> 0)
      const discount = (100 - discountPercent) / 100

      this.isApplyingDiscount = true
      const payload = {
        products: [{ productId: product.productId, discount }],
      }

      try {
        const res = await store.dispatch(UPDATE_ORDER_PRODUCTS_DISCOUNT, {
          orderId: this.order._id,
          payload,
        })
        apiToastSuccess(res.data.message || 'Discount applied successfully!')
        this.$set(this.editingDiscountProductIds, product.productId, false)
        this.$emit('reload-order')
      } catch (error) {
        apiToastErrorV2(error)
      } finally {
        this.isApplyingDiscount = false
      }
    },
    initProductDiscounts() {
      // Initialize discount values from existing product data
      if (!this.orderProducts) return

      this.orderProducts.forEach(product => {
        const hasDiscount = product.discount !== undefined && product.discount !== null && product.discount < 1
        if (hasDiscount) {
          // Convert multiplier back to percentage (e.g., 0.85 -> 15, 0 -> 100)
          const discountPercent = Math.round((1 - product.discount) * 100)
          this.$set(this.productDiscounts, product.productId, discountPercent)
        }
      })
    },
    togglePartialBreakdown(productId) {
      this.$set(
        this.expandedPartialProducts,
        productId,
        !this.expandedPartialProducts[productId],
      )
    },
    isPartiallyShipped(product) {
      return product.status === ORDER_PRODUCT_SHIPMENT_STATUS.PARTIALLY_SHIPPED.LABEL
    },
    hasShippedItems(product) {
      // For carton products, check shippedQuantity
      if (product.isCarton) {
        return (product.shippedQuantity || 0) > 0
      }
      return product.shippedItems && product.shippedItems.length > 0
    },
    getRemainingUnits(product) {
      // For carton products, use quantity (cartons) not lineTotalQuantity (units)
      if (product.isCarton) {
        const originalQty = product.originalQuantity || product.quantity || 0
        return originalQty - (product.shippedQuantity || 0)
      }
      if (!product.lineTotalQuantity) return 0
      return (product.lineTotalQuantity || 0) - (product.shippedQuantity || 0)
    },
    getShippedItemsForDisplay(product) {
      if (!product.items || !product.shippedItems) return []
      const shippedMap = {}
      product.shippedItems.forEach(shipped => {
        shippedMap[shipped.skuId] = shipped.shippedValue || 0
      })
      return product.items.map(item => ({
        ...item,
        value: shippedMap[item.skuId || item.sku] || 0,
      }))
    },
    getRemainingItemsForDisplay(product) {
      if (!product.items || !product.shippedItems) return []
      const shippedMap = {}
      product.shippedItems.forEach(shipped => {
        shippedMap[shipped.skuId] = shipped.shippedValue || 0
      })
      return product.items.map(item => {
        const shipped = shippedMap[item.skuId || item.sku] || 0
        const remaining = (item.value || 0) - shipped
        return {
          ...item,
          value: remaining > 0 ? remaining : 0,
        }
      })
    },
  },
  emits: ['update-total', 'update-total-units', 'remove-product', 'ship-now', 'reload-order', 'partial-confirm-selection'],
  setup(props, {
    emit,
  }) {
    onMounted(() => {
      window.addEventListener('scroll', onScrollLoader)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', onScrollLoader)
    })

    const variantLoaded = ref(false)
    const isLoaded = ref(false)
    const isSelectedProduct = ref(false)
    const products = ref([])
    const productsSlice = ref([])
    const colorHeading = 'color'
    const headings = ref([{
      key: colorHeading,
      label: 'Color',
    }])

    let [productsSliceX, productsSliceY, pageNumber] = [0, 0, 1]
    productsSlice.value = []
    // KP-4363: products matching the search box; `products` keeps the full list
    // because shipment selection, ship and totals read it.
    const productSearchQuery = ref('')
    let visibleProducts = []

    const onScrollLoader = () => {
      if (window.scrollY > (document.body.scrollHeight - 2000) && visibleProducts.length > productsSliceX) {
        pageNumber += 1
        productsSliceY = visibleProducts.length < (pageNumber * PRODUCTS_LIMIT) ? visibleProducts.length : pageNumber * PRODUCTS_LIMIT
        visibleProducts.slice(productsSliceX, productsSliceY).forEach(p => productsSlice.value.push(p))
        productsSliceX = productsSliceY
      }
    }

    // Restarts the paginated list from the products matching the search.
    const resetProductsSlice = () => {
      productsSlice.value = []
      visibleProducts = filterOrderProductsBySearch(products.value, productSearchQuery.value) || []
      pageNumber = 1
      productsSliceY = visibleProducts.length < PRODUCTS_LIMIT ? visibleProducts.length : PRODUCTS_LIMIT
      visibleProducts.slice(0, productsSliceY).forEach(p => productsSlice.value.push(p))
      productsSliceX = productsSliceY
    }

    const { parseProductImage } = useEcommerceUi()
    const partialConfirm = useOrderPartialConfirm()

    // Helper function to ensure correct quantity structure
    const ensureCorrectQuantityStructure = () => {
      // Ensure non-carton products have product.quantity = 1
      props.order.products.forEach(product => {
        if (!product.isCarton) {
          product.quantity = 1
        }
      })
    }

    const updateTotalPriceAndUnits = () => {
      const role = store.getters.entityType
      getProductAndOrderTotals(role, props.order)
    }

    const onCartonQuantityChange = () => {
      updateTotalPriceAndUnits()
    }

    const setOrderProducts = () => {
      productsSlice.value = []
    // receive products from order details
    if (props.orderProducts && props.orderProducts.length) {
        products.value = props.orderProducts
        // Pagination done here
        resetProductsSlice()
        isLoaded.value = true
        // Ensure correct quantity structure after products are set
        ensureCorrectQuantityStructure()
    }
    }

    // Setting order products
    setOrderProducts()

    // Extract SKU values from variants into a map
    const extractVariantValues = variants => {
      const currentItems = {}
      variants.forEach(variant => {
        Object.values(variant).forEach(cell => {
          if (cell && cell.skuId) {
            currentItems[cell.skuId] = cell.value
          }
        })
      })
      return currentItems
    }

    // Update itemsForShipment with values from variants
    const updateItemsForShipment = (product, currentItems) => {
      product.itemsForShipment.forEach(item => {
        const key = item.skuId || item.sku
        if (key && Object.prototype.hasOwnProperty.call(currentItems, key)) {
          item.value = currentItems[key] || 0
        }
      })
    }

    // Check if in shipment creation mode
    const isShipmentCreationMode = product => props.order?.isDataForCreateShipment && product.itemsForShipment

    // update product variant => recalculate total
    const updateProductVariants = (variants, product) => {
      product.variants = variants

      if (isShipmentCreationMode(product)) {
        const currentItems = extractVariantValues(variants)
        updateItemsForShipment(product, currentItems)
      } else {
        const newItems = parseProductItems(product)
        // Guard: only replace product.items when items have actually changed.
        // parseProductItems returns a new array ref via .map(), which triggers
        // the ProductVariantsMixin `items` watcher → re-emits `update-variants`
        // → calls this function again → infinite loop.
        const prevItems = product.items
        const shouldReplace = newItems !== prevItems
          && (!prevItems || prevItems.length !== newItems.length
            || newItems.some((item, idx) => item !== prevItems[idx]))
        if (shouldReplace) {
          product.items = newItems
        }
      }

      if (!product.isCarton) {
        product.quantity = 1
      }
      updateTotalPriceAndUnits()
      variantLoaded.value = true
    }

    // On select product for shipment
    const onSelect = (isSelected, product, index) => {
      if (props.isShipment) {
        updateTotalPriceAndUnits()
      }

      if (isSelected) {
        // Store original status before changing to selectedByUser
        product.originalStatus = product.status
        product.status = ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
      } else {
        // Restore original status (partiallyShipped) or default to readyToShip
        product.status = product.originalStatus || ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL
      }

      // The slice can be filtered, so its index is not the index in `products`.
      const productIndex = products.value.indexOf(product)
      Vue.set(products.value, productIndex === -1 ? index : productIndex, product)
      const isSelectedAtleastOne = products.value.find(
        product => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
      )
      isSelectedProduct.value = !!isSelectedAtleastOne
    }

    // On select all ready to ship products
    const onSelectReadyToShipProducts = isSelected => {
      for (let i = 0; i < products.value.length; i++) {
        if (isLineShipSelectable(products.value[i], props.order)) {
          if (isSelected) {
            // Store original status before changing to selectedByUser
            products.value[i].originalStatus = products.value[i].status
            products.value[i].status = ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
          } else {
            // Restore original status (partiallyShipped) or default to readyToShip
            products.value[i].status = products.value[i].originalStatus || ORDER_PRODUCT_SHIPMENT_STATUS.READY_TO_SHIP.LABEL
          }
          Vue.set(products.value, i, products.value[i])
        }
      }
      isSelectedProduct.value = isSelected
    }

    // Check if product is selected for shipment
    const isProductSelectedForShipment = product => product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL
      && getStatusTextAndColorCode(product.status, props.order).isEnabled

    // Build a map of shipped quantities by SKU ID
    const buildShippedQuantityMap = shippedItems => {
      const shippedMap = {}
      if (shippedItems?.length > 0) {
        shippedItems.forEach(shipped => {
          shippedMap[shipped.skuId] = shipped.shippedValue || 0
        })
      }
      return shippedMap
    }

    // Set carton shipment quantities
    const setCartonShipmentQty = productCopy => {
      const originalQty = productCopy.quantity || 0
      const shippedCartons = productCopy.shippedQuantity || 0
      productCopy.maxShipmentQty = originalQty - shippedCartons
      productCopy.originalQuantity = originalQty
      productCopy.quantity = productCopy.maxShipmentQty
    }

    // Build itemsForShipment array with remaining quantities
    const buildItemsForShipment = (items, shippedMap) => items.map(item => {
      const itemCopy = JSON.parse(JSON.stringify(item))
      const skuId = item.skuId || item.sku
      const shippedQty = shippedMap[skuId] || 0
      itemCopy.maxShipmentQty = (item.value || 0) - shippedQty
      itemCopy.value = itemCopy.maxShipmentQty
      return itemCopy
    })

    // Prepare a single product for shipment
    const prepareProductForShipment = product => {
      const productCopy = JSON.parse(JSON.stringify(product))
      const shippedMap = buildShippedQuantityMap(productCopy.shippedItems)

      if (productCopy.isCarton) {
        setCartonShipmentQty(productCopy)
      }

      productCopy.itemsForShipment = buildItemsForShipment(productCopy.items, shippedMap)
      return productCopy
    }

    // On click ship products
    const shipProducts = () => {
      const orderData = JSON.parse(JSON.stringify(props.order))
      const selectedProducts = products.value
        .filter(isProductSelectedForShipment)
        .map(prepareProductForShipment)

      orderData.isDataForCreateShipment = true
      orderData.products = selectedProducts
      emit('ship-now', orderData)
    }

    // Checking is selected all
    const isSelectedAll = () => {
      if (isSelectedProduct.value) {
        const selectedProducts = products.value.filter(
          product => product.status
            === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
        )
        return props.order.readyToShipProductsCount === selectedProducts.length
      }
      return false
    }

    return {
      products,
      productsSlice,
      isLoaded,
      isSelectedProduct,
      variantLoaded,
      onSelect,
      onSelectReadyToShipProducts,
      isSelectedAll,
      shipProducts,
      formattedDate,

      // UI
      parseProductImage,
      updateProductVariants,

      // Filter
      formatNumber,
      formatCurrency,
      headings,
      colorHeading,
      updateTotalPriceAndUnits,
      onCartonQuantityChange,
      setOrderProducts,
      productSearchQuery,
      searchOrderProducts: query => {
        productSearchQuery.value = query || ''
        resetProductsSlice()
      },

      // KP-4363 partial confirm
      selectedProductIds: partialConfirm.selectedProductIds,
      isPartialConfirmEnabled: partialConfirm.isPartialConfirmEnabled,
      isLineSelectableForConfirm: partialConfirm.isLineSelectableForConfirm,
      isLineSelectedForConfirm: partialConfirm.isLineSelectedForConfirm,
      toggleLineForConfirm: partialConfirm.toggleLineForConfirm,
      getSelectedForConfirmIds: partialConfirm.getSelectedForConfirmIds,
      isAllPendingSelected: partialConfirm.isAllPendingSelected,
      toggleAllPendingForConfirm: partialConfirm.toggleAllPendingForConfirm,
      clearConfirmSelection: partialConfirm.clearConfirmSelection,
      isConfirmedLine: partialConfirm.isConfirmedLine,
      isLockedConfirmedLine: partialConfirm.isLockedConfirmedLine,
      isPartiallyConfirmedOrder: partialConfirm.isPartiallyConfirmedOrder,
      getShipSelectAllLabel,
      isPendingConfirmLine: partialConfirm.isPendingConfirmLine,
    }
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-ecommerce.scss';
@import '~@core/scss/base/pages/app-cart-products.scss';

// KP-4363: search box above the order products
.order-products-search {
  max-width: 360px;
}

// Carton quantity validation for shipment
.carton-qty-invalid {
  border: 2px solid #ea5455 !important;
  border-radius: 4px;

  .btn {
    border-color: #ea5455 !important;
  }
}
</style>
